import React, { Dispatch, useEffect, useReducer, useRef, useState } from "react";

import { ComboBoxCloseEvent, ComboBoxFilterChangeEvent, DropDownListChangeEvent, MultiSelectChangeEvent, MultiSelectFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { Input, InputChangeEvent, NumericTextBox, NumericTextBoxChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import ApiCommunicator, { useApiService } from "@selas/api-communication";
import { IAction, IEntity } from "@selas/models";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, ManageableField, ManageableMultiSelect, SearchBox, setEntity, TextArea, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import map from "lodash/map";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { hasPermission } from "../../../services/authentication";
import { catalogueServiceReducer, invoicingGroupReducer, invoicingMomentReducer, languageReducer, serviceTypeReducer } from "../../../state/reducers";
import { Permission } from "../../../utils/enums";
import { ICatalogueService, IInvoicingGroup, IInvoicingMoment, ILanguage, IServiceLanguage, IServiceType } from "../../../utils/types/models";
import InvoicingMomentEditor from "../masterdata/invoicingMoment";
import LanguageEditor from "../personInfo/language";
import InvoicingGroupEditor from "./invoicingGroup";
import ServiceTypeEditor from "./serviceType";

const CatalogueServiceEditor: React.FC<IAddEntityScreenProps<ICatalogueService> | IEditEntityScreenProps<ICatalogueService>> = (
	props: IAddEntityScreenProps<ICatalogueService> | IEditEntityScreenProps<ICatalogueService>
) => {
	const { t } = useTranslation();

	const initialCatalogueService: ICatalogueService = {
		id: (props as IEditEntityScreenProps<ICatalogueService>).recordId || 0,
		code: "",
		typeId: null,
		languages: [],
		collectiveHours: 0,
		individualHours: 0,
		numberOfMonths: 0,
		invoicingMomentId: null,
		salesPrice: 0,
		account: undefined,
		isProjectLevelService: false,
		active: true
	};
	const [catalogueService, setCatalogueService] = useState<ICatalogueService>(initialCatalogueService);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const [catalogueServiceState, catalogueServiceDispatch] = useReducer(catalogueServiceReducer, getInitialState<ICatalogueService>());
	const [invoicingGroupState, invcoingGroupDispatch] = useReducer(invoicingGroupReducer, getInitialState<IInvoicingGroup>());
	const [serviceTypeState, serviceTypeDispatch] = useReducer(serviceTypeReducer, getInitialState<IServiceType>());
	const [languageState, languageDispatch] = useReducer(languageReducer, getInitialState<ILanguage>());
	const [invoicingMomentState, invoicingMomentDispatch] = useReducer(invoicingMomentReducer, getInitialState<IInvoicingMoment>());
	const firstField: React.MutableRefObject<Input> = useRef();
	const apiService: ApiCommunicator = useApiService();

	useEffect(() => {
		if (catalogueServiceState.entity) {
			setCatalogueService(catalogueServiceState.entity);
		}
	}, [catalogueServiceState.entity]);

	useEffect(() => {
		if (hasPermission(Permission.CatalogueServicesAdd, Permission.CatalogueServicesUpdate)) {
			apiService.callApi(languageDispatch, Endpoint.Languages, "GET");
			apiService.callApi(invcoingGroupDispatch, Endpoint.InvoicingGroups, "GET");
		}
	}, [apiService]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent | ComboBoxCloseEvent | DropDownListChangeEvent | React.ChangeEvent<HTMLTextAreaElement> | NumericTextBoxChangeEvent): void {
		setCatalogueService(handleChange(catalogueService, event));
		setDataChanged(true);
	}

	function setChild<T extends IEntity>(entity: T, field: keyof ICatalogueService, idField: keyof ICatalogueService): void {
		const newContact: ICatalogueService = setEntity(catalogueService, entity, field, idField);
		updateCatalogueService(newContact);
	}

	function onMultiSelectChange(event: MultiSelectChangeEvent): void {
		const newCatalogueService: ICatalogueService = cloneDeep(catalogueService);

		if (event.value) {
			if (event.target.name === "languages") {
				newCatalogueService.languages = [];
				for (const lang of event.value) {
					newCatalogueService.languages.push({ serviceId: newCatalogueService.id, languageId: lang.id, language: lang });
				}
			}
			updateCatalogueService(newCatalogueService);
		}
	}

	function addOrUpdateLanguage(language: ILanguage): void {
		const newCatalogueService: ICatalogueService = cloneDeep(catalogueService);
		const existingServiceLanguage: IServiceLanguage<ICatalogueService> = find(newCatalogueService.languages, { languageId: language.id });
		if (existingServiceLanguage) {
			existingServiceLanguage.language = language;
		} else {
			newCatalogueService.languages.push({
				serviceId: newCatalogueService.id,
				languageId: language.id,
				language
			});
		}
		apiService.callApi(languageDispatch, Endpoint.Languages, "GET");
		updateCatalogueService(newCatalogueService);
	}

	function updateCatalogueService(newCatalogueService: ICatalogueService): void {
		setCatalogueService(newCatalogueService);
		setDataChanged(true);
	}

	function onFilterChange(event: ComboBoxFilterChangeEvent | MultiSelectFilterChangeEvent): void {
		let dispatch: Dispatch<IAction>;
		let filterEndpoint: Endpoint;
		switch (event.target.name) {
			case "invoicingGroupId":
				dispatch = invcoingGroupDispatch;
				filterEndpoint = Endpoint.InvoicingGroups;
				break;
			case "typeId":
				dispatch = serviceTypeDispatch;
				filterEndpoint = Endpoint.ServiceTypes;
				break;
			case "invoicingMomentId":
				dispatch = invoicingMomentDispatch;
				filterEndpoint = Endpoint.InvoicingMoments;
				break;
			case "languages":
				dispatch = languageDispatch;
				filterEndpoint = Endpoint.Languages;
				break;
		}
		apiService.callApi(dispatch, filterEndpoint, "GET", { search: event.filter.value });
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(catalogueService.code)) {
			messages.push(t("fill_in_required_field", { field: t("serviceCode").toLowerCase() }));
		}
		if (!catalogueService.typeId) {
			messages.push(t("fill_in_required_field", { field: t("serviceType").toLowerCase() }));
		}
		if (!catalogueService.invoicingMomentId) {
			messages.push(t("fill_in_required_field", { field: t("invoicingMoment").toLowerCase() }));
		}
		if (!catalogueService.languages || catalogueService.languages.length <= 0) {
			messages.push(t("fill_in_required_field", { field: t("languages").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: ICatalogueService): void {
		if (record && !close) {
			setCatalogueService(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<ICatalogueService>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={catalogueService}
			endpoint={Endpoint.CatalogueServices}
			entityState={catalogueServiceState}
			entityType={t("catalogueService")}
			dispatch={catalogueServiceDispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={catalogueService.code}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<div className="row">
					<div className="col">
						<label className="k-form-field">
							<span>{t("serviceCode")} *</span>
							<Input name="code" ref={firstField} value={catalogueService.code} onChange={onChange} required disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<ManageableField
							fieldLabel={`${t("invoicingGroup")}`}
							recordId={catalogueService.invoicingGroupId}
							addScreen={{ screen: InvoicingGroupEditor, isAllowed: hasPermission(Permission.InvoicingGroupsAdd) }}
							editScreen={{ screen: InvoicingGroupEditor, isAllowed: hasPermission(Permission.InvoicingGroupsUpdate) }}
							setEntity={(invoicingGroup: IInvoicingGroup) => setChild(invoicingGroup, "invoicingGroup", "invoicingGroupId")}
							readOnly={readonly}
						>
							<SearchBox
								name="invoicingGroupId"
								entities={invoicingGroupState.entities}
								isLoading={invoicingGroupState.areEntitiesLoading}
								entityId={catalogueService.invoicingGroupId}
								entity={catalogueService.invoicingGroup}
								textField="name"
								onFilterChange={onFilterChange}
								onClose={onChange}
								onClear={() => setChild(null, "invoicingGroup", "invoicingGroupId")}
								disabled={readonly}
							/>
						</ManageableField>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<ManageableField
							fieldLabel={`${t("serviceType")} *`}
							recordId={catalogueService.typeId}
							addScreen={{ screen: ServiceTypeEditor, isAllowed: hasPermission(Permission.ServiceTypesAdd) }}
							editScreen={{ screen: ServiceTypeEditor, isAllowed: hasPermission(Permission.ServiceTypesUpdate) }}
							setEntity={(type: IServiceType) => setChild(type, "type", "typeId")}
							readOnly={readonly}
						>
							<SearchBox
								name="typeId"
								entities={serviceTypeState.entities}
								isLoading={serviceTypeState.areEntitiesLoading}
								entityId={catalogueService.typeId}
								entity={catalogueService.type}
								required
								textField="name"
								onFilterChange={onFilterChange}
								onClose={onChange}
								onClear={() => setChild(null, "type", "typeId")}
								disabled={readonly}
							/>
						</ManageableField>
					</div>
					<div className="col">
						<ManageableMultiSelect
							fieldLabel={`${t("languages")} *`}
							addScreen={{ screen: LanguageEditor, isAllowed: hasPermission(Permission.LanguagesAdd) }}
							editScreen={{ screen: LanguageEditor, isAllowed: hasPermission(Permission.LanguagesUpdate) }}
							setEntity={addOrUpdateLanguage}
							name="languages"
							data={languageState.entities}
							textField="name"
							dataItemKey="id"
							value={map(catalogueService.languages, "language")}
							loading={languageState.areEntitiesLoading}
							onChange={onMultiSelectChange}
							filterable
							onFilterChange={onFilterChange}
							autoClose={false}
							disabled={readonly}
							readOnly={readonly}
							required
						/>
					</div>
				</div>
				<label className="k-form-field">
					<span>{t("shortDescription")}</span>
					<TextArea name="shortDescription" value={catalogueService.shortDescription} onChange={onChange} disabled={readonly} />
				</label>
				<label className="k-form-field">
					<span>{t("longDescription")}</span>
					<TextArea name="longDescription" value={catalogueService.longDescription} onChange={onChange} disabled={readonly} />
				</label>
				<div className="row">
					<div className="col">
						<label className="k-form-field">
							<span>{t("collectiveHours")}</span>
							<NumericTextBox name="collectiveHours" value={catalogueService.collectiveHours} onChange={onChange} format="n2" min={0} disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("individualHours")}</span>
							<NumericTextBox name="individualHours" value={catalogueService.individualHours} onChange={onChange} format="n2" min={0} disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("numberOfMonths")}</span>
							<NumericTextBox name="numberOfMonths" value={catalogueService.numberOfMonths} onChange={onChange} format="n0" min={0} disabled={readonly} />
						</label>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<ManageableField
							fieldLabel={`${t("invoicingMoment")} *`}
							recordId={catalogueService.invoicingMomentId}
							addScreen={{ screen: InvoicingMomentEditor, isAllowed: hasPermission(Permission.InvoicingMomentsAdd) }}
							editScreen={{ screen: InvoicingMomentEditor, isAllowed: hasPermission(Permission.InvoicingMomentsUpdate) }}
							setEntity={(invoicingMoment: IInvoicingMoment) => setChild(invoicingMoment, "invoicingMoment", "invoicingMomentId")}
							readOnly={readonly}
						>
							<SearchBox
								name="invoicingMomentId"
								entities={invoicingMomentState.entities}
								isLoading={invoicingMomentState.areEntitiesLoading}
								entityId={catalogueService.invoicingMomentId}
								entity={catalogueService.invoicingMoment}
								required
								textField="name"
								onFilterChange={onFilterChange}
								onClose={onChange}
								onClear={() => setChild(null, "invoicingMoment", "invoicingMomentId")}
								disabled={readonly}
							/>
						</ManageableField>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("salesPrice")}</span>
							<NumericTextBox name="salesPrice" value={catalogueService.salesPrice} onChange={onChange} format="n2" disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("account")}</span>
							<Input name="account" value={catalogueService.account} onChange={onChange} disabled={readonly} />
						</label>
					</div>
				</div>
				<div className="k-form-field">
					<div>{t("isProjectLevelService")}</div>
					<YesNoSwitch name="isProjectLevelService" checked={catalogueService.isProjectLevelService} onChange={onChange} disabled={readonly} />
				</div>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={catalogueService.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default CatalogueServiceEditor;
